import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, Film, ArrowRight, Tv, Megaphone, Tv2, PartyPopper, Sparkles, Clapperboard,
  Lock, CalendarDays, Trophy,
} from "lucide-react";
import { toast } from "sonner";

// TV-shaped projects belong on /p/:id (the TV dashboard with Script
// Change Log, Episode Signoff, Cast Approval). Everything else keeps
// the Virtual Production Office landing at /projects/:id/cubicles.
const TV_TYPES = new Set([
  "tv_episodic", "tv_daily_soap", "format_reality",
  "tv", "format_show", // legacy aliases
]);
const projectHref = (p) => TV_TYPES.has(p.project_type)
  ? `/p/${p.id}`
  : `/projects/${p.id}/cubicles`;

const TYPE_ICONS = {
  film:           Film,
  short_film:     Clapperboard,
  ad_film:        Megaphone,
  tv:             Tv,           // legacy alias, normalized on backend
  tv_episodic:    Tv,
  tv_daily_soap:  CalendarDays,
  format_reality: Trophy,
  web_series:     Tv2,
  micro_drama:    Sparkles,
  format_show:    PartyPopper,   // legacy alias
  event:          PartyPopper,
};

export default function Projects() {
  const nav = useNavigate();
  const { featureFlags } = useAuth();
  const paymentsOff = featureFlags && featureFlags.payments_enabled === false;
  const [projects, setProjects] = useState([]);
  const [types,    setTypes]    = useState([]);
  const [defaultType, setDefaultType] = useState("");
  const [open,     setOpen]     = useState(false);

  // Wizard state
  const [step, setStep] = useState(0);          // 0 = pick type, 1 = name
  const [picked, setPicked] = useState(null);
  const [title, setTitle]   = useState("");
  const [location, setLocation] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    const { data } = await api.get("/projects");
    setProjects(data);
  };

  useEffect(() => {
    api.get("/meta/project-types").then(({ data }) => {
      // Hide legacy aliases from the picker — TYPE_ICONS still maps them
      // so pre-existing project cards render their icon correctly.
      setTypes((data || []).filter((t) => !t.legacy && t.key !== "event"));
    });
    api.get("/production-office/default-project-type")
       .then(({ data }) => {
         setDefaultType(data.default_project_type || "ad_film");
       })
       .catch(() => setDefaultType("ad_film"));
    refresh();
  }, []);

  // Filter dropdown options + resolve the pre-selected default
  const visibleTypes = useMemo(() => types, [types]);
  const smartDefault = useMemo(
    () => visibleTypes.find((t) => t.key === defaultType) || visibleTypes[0] || null,
    [visibleTypes, defaultType],
  );

  const openWizard = () => {
    setStep(0);
    setTitle(""); setLocation("");
    // Predictive pre-select — user can override by clicking any tile.
    setPicked(smartDefault);
    setOpen(true);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/projects/init", {
        title, project_type: picked.key, location,
      });
      setOpen(false);
      toast.success(`${data.title} created — opening virtual production office`);
      // TV-shaped projects land straight on the dashboard (/p/:id) so the
      // Aired-On / Bank / Multi-Unit tiles are visible immediately. Everything
      // else keeps the existing Virtual Production Office landing.
      nav(projectHref(data));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="projects-page">
      <div className="flex justify-between items-end mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Production</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">Projects</h1>
          <p className="mt-2 text-sm text-white/55">
            {paymentsOff
              ? "Spin up shoots. Pick a format. Every dashboard unlocks instantly during our free beta."
              : "Spin up shoots. Pick a format. Pay to unlock the operating dashboard."}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openWizard}
              className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
              data-testid="new-project-btn">
              <Plus size={14} className="mr-2" /> New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-2xl" data-testid="new-project-dialog">
            <DialogHeader>
              <DialogTitle className="font-display uppercase tracking-tight text-xl">
                {step === 0 ? "Pick a format" : "Name the project"}
              </DialogTitle>
              <DialogDescription className="text-white/55 text-xs">
                {step === 0
                  ? "Each format is bound to a subscription tier. Payment unlocks the project dashboard."
                  : `${picked?.label} · ${picked?.plan?.replace("_", " ").toUpperCase()} tier`}
              </DialogDescription>
            </DialogHeader>

            {step === 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2" data-testid="proj-type-grid">
                {visibleTypes.map((t) => {
                  const Icon = TYPE_ICONS[t.key] || Film;
                  const isSuggested = smartDefault?.key === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => { setPicked(t); setStep(1); }}
                      className={
                        "cs-card rounded-xl p-4 text-left transition-colors group relative " +
                        (isSuggested ? "bg-white/[0.06]" : "hover:bg-white/[0.06]")
                      }
                      data-testid={`proj-type-${t.key}`}
                    >
                      {isSuggested && (
                        <span className="absolute top-2 right-2 text-[9px] tracking-[0.22em] uppercase text-emerald-300"
                              data-testid="proj-type-suggested-tag">
                          Suggested
                        </span>
                      )}
                      <div className="w-9 h-9 rounded-lg bg-white/8 flex items-center justify-center text-white/85 group-hover:text-[color:var(--association-theme)]">
                        <Icon size={17} />
                      </div>
                      <div className="mt-3 text-sm font-semibold text-white">{t.label}</div>
                      <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-white/45">
                        {t.plan.replace("_", " ")} tier
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-4 mt-2">
                <div>
                  <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Title *</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)}
                    placeholder={picked?.key === "ad_film" ? "e.g. Tata Salt — Diwali TVC" : "e.g. Untitled Feature"}
                    className="mt-2 h-11 bg-black/40 border-white/15"
                    data-testid="proj-title-input" autoFocus />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Location</Label>
                  <Input value={location} onChange={(e) => setLocation(e.target.value)}
                    placeholder="Mumbai · Goregaon Film City"
                    className="mt-2 h-11 bg-black/40 border-white/15"
                    data-testid="proj-location-input" />
                </div>
                <div className="flex items-center justify-between pt-2">
                  <button onClick={() => setStep(0)} className="text-[10px] tracking-[0.22em] uppercase text-white/55 hover:text-white"
                    data-testid="proj-back">
                    ← Pick a different format
                  </button>
                  <Button onClick={submit} disabled={!title || submitting}
                    className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
                    data-testid="proj-create-submit">
                    {submitting ? "Creating…" : "Create →"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {projects.length === 0 ? (
        <div className="cs-card rounded-2xl p-12 text-center">
          <Film size={36} className="mx-auto text-white/30 mb-4" />
          <div className="font-display text-2xl uppercase tracking-tight">No projects yet</div>
          <div className="mt-2 text-sm text-white/55">Hit &quot;New Project&quot; to scaffold your first shoot.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((p) => (
            <Link key={p.id} to={projectHref(p)}
              className="cs-card rounded-2xl p-6 hover:translate-y-[-2px] transition relative"
              data-testid={`project-card-${p.id}`}>
              {!paymentsOff && p.status === "awaiting_payment" && (
                <Lock size={13} className="absolute top-4 right-4 text-amber-300/70" />
              )}
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">
                {p.type_label || p.project_type || p.plan?.replace("_", " ")}
              </div>
              <h3 className="font-display text-2xl uppercase tracking-tight mt-1.5">{p.title}</h3>
              <div className="mt-3 text-xs text-white/55">{p.location || "Location TBD"}</div>
              <div className="mt-6 flex items-center justify-between border-t border-white/5 pt-4">
                <span className="text-[10px] tracking-[0.25em] uppercase" style={{ color: "var(--association-theme)" }}>
                  {!paymentsOff && p.status === "awaiting_payment"
                    ? "Locked · Pay to unlock"
                    : (p.status === "awaiting_payment"
                        ? "Pre production"
                        : (p.status || "active").replace("_", " "))}
                </span>
                <ArrowRight size={14} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
